import type { Direction } from '../types/optimise';
import type { Tariff, RegionResponse, SupportedTariffsResponse } from '../types/tariff';

export const OPTIMISE_NAME = 'Optimise';
export const ZERO_RATE_EXPORT_TARIFF_ID = 'zero-rate-export';

const getZeroRateExportTariff = (suppliers?: Tariff[]): Tariff | undefined => {
  if (!Array.isArray(suppliers)) {
    return undefined;
  }
  const result = suppliers.find(
    (supplier) => supplier.tariff_id?.endsWith(ZERO_RATE_EXPORT_TARIFF_ID) && supplier.supplier_name === OPTIMISE_NAME
  );
  return result;
};

/**
 * Gets the zero rate export tariff ID for a given postcode
 * Used when a user doesn't have an export tariff yet during onboarding
 *
 * @param {string | undefined} postcode - The user's postcode
 * @param {Tariff[] | undefined} exportTariffs - Optional array of export tariffs to use instead of making an API call
 * @returns {Promise<string | undefined>} - The zero rate export tariff ID, or undefined if not found
 *
 */
const getZeroRateExportTariffId = async (postcode?: string, exportTariffs?: Tariff[]): Promise<string | undefined> => {
  // If export tariffs are provided, use them directly
  if (exportTariffs && Array.isArray(exportTariffs)) {
    const zeroRateTariff = getZeroRateExportTariff(exportTariffs);
    return zeroRateTariff?.tariff_id;
  }

  // Otherwise, fetch from API
  if (!postcode) {
    return undefined;
  }

  try {
    // Dynamic imports to avoid circular dependencies
    const { getRegion } = await import('../api/Location');
    const { getSupportedTariffs, TariffDirection } = await import('../api/Optimise');

    const region = (await getRegion(postcode)) as RegionResponse | undefined;
    if (!region?.distribution_id) {
      return undefined;
    }

    const response = (await getSupportedTariffs(TariffDirection.EXPORT as Direction, region.distribution_id)) as
      | SupportedTariffsResponse
      | undefined;
    const fetchedExportTariffs = response?.supported_tariffs;

    if (!Array.isArray(fetchedExportTariffs)) {
      return undefined;
    }

    const zeroRateTariff = getZeroRateExportTariff(fetchedExportTariffs);
    return zeroRateTariff?.tariff_id;
  } catch (error) {
    console.error('Error fetching zero rate export tariff:', error);
    return undefined;
  }
};

/**
 * Gets the final export tariff ID, using zero rate export tariff if none is provided
 * Used when a user doesn't have an export tariff yet during onboarding or tariff updates
 *
 * @param {string | undefined} exportTariffId - The provided export tariff ID
 * @param {string | undefined} postcode - The user's postcode
 * @param {Tariff[] | undefined} exportTariffs - Optional array of export tariffs to use instead of making an API call
 * @returns {Promise<string | undefined>} - The final export tariff ID, or undefined if not found
 *
 * @example
 * const finalTariffId = await getFinalExportTariffId(undefined, 'SW1A 1AA');
 * // Returns zero rate export tariff ID for the postcode
 *
 * @example
 * const finalTariffId = await getFinalExportTariffId('existing-tariff-id', 'SW1A 1AA');
 * // Returns 'existing-tariff-id'
 *
 * @example
 * const finalTariffId = await getFinalExportTariffId(undefined, 'SW1A 1AA', existingExportTariffs);
 * // Uses provided tariffs instead of making API call
 */
const getFinalExportTariffId = async (
  exportTariffId?: string,
  postcode?: string,
  exportTariffs?: Tariff[]
): Promise<string | undefined> => {
  // If exportTariffId is provided, return it
  if (exportTariffId) {
    return exportTariffId;
  }

  // Otherwise, try to get the zero rate export tariff ID using the postcode
  if (!postcode) {
    return undefined;
  }

  try {
    const zeroRateTariffId = await getZeroRateExportTariffId(postcode, exportTariffs);
    if (!zeroRateTariffId) {
      console.warn('Could not find zero rate export tariff for postcode:', postcode);
    }
    return zeroRateTariffId;
  } catch (error) {
    console.error('Error fetching zero rate export tariff:', error);
    return undefined;
  }
};

const getTariffById = (id: string, tariffs: Tariff[]): Tariff | null => {
  return tariffs.find((tariff) => tariff.tariff_id === id) || null;
};

const getTariffsForSupplier = (supplierName: string, tariffs: Tariff[]): Tariff[] => {
  const supplierTariffs: Tariff[] = [];
  tariffs.forEach((tariff) => {
    if (supplierName === tariff.supplier_name) {
      supplierTariffs.push(tariff);
    }
  });
  return supplierTariffs;
};

const getSupplierNames = (tariffs: Tariff[]): string[] => {
  const supplierNames: string[] = [];
  tariffs.forEach(({ supplier_name }: { supplier_name: string }) => {
    if (!supplierNames.includes(supplier_name) && OPTIMISE_NAME !== supplier_name) {
      supplierNames.push(supplier_name);
    }
  });

  return supplierNames.sort((a, b) => a.localeCompare(b));
};

export {
  getZeroRateExportTariff,
  getZeroRateExportTariffId,
  getFinalExportTariffId,
  getTariffById,
  getTariffsForSupplier,
  getSupplierNames,
};
